// @flow

import { selectPricingInfos } from '@gandi/billing';

import { useResourceFetch } from '@gandi/resource/react';

import { selectShopCurrent } from 'src/modules/organization/selectors';
import { tldPricesResource } from 'src/modules/tldpublic/tlds';
import type { TldPriceSummary } from 'src/modules/tldpublic/tlds';

import { useAppSelector } from './useAppSelector';
import { useFeatureFlags } from './useFeatureFlag';

const eligibleGrids = ['A', 'B', 'C', 'D', 'E'] as const;
export function useIsRetailCustomer(): boolean {
  const cartOwnerOrga = useAppSelector(selectShopCurrent);
  const { grid } = useAppSelector(selectPricingInfos);
  const isRetail =
    // Unauthenticated users are eligible
    !cartOwnerOrga ||
    // Authenticated users are eligible if their billed organization is retail (not corpo/reseller)
    ((!cartOwnerOrga.reseller || !cartOwnerOrga.corporate) &&
      // and doesn't have a negotiated pricing grid
      eligibleGrids.includes(grid));

  return isRetail;
}

/** Checks if a domain's TLD is present in a set of eligible TLDs. */
export function isTldEligible(
  domain: string,
  eligibleTldsData: Array<TldPriceSummary>,
): boolean {
  const eligibleTlds = new Set(
    eligibleTldsData.flatMap(tld => [tld.name, tld.alabel].filter(Boolean)),
  );

  // Get all possible TLDs from the domain (for example "example.co.uk" will yield "uk" and "co.uk")
  const potentialTlds = domain
    .split('.')
    .map((_, index, parts) => parts.slice(index).join('.'))
    .slice(1); // Exclude the full domain itself

  return potentialTlds.some(potentialTld => eligibleTlds.has(potentialTld));
}

function useIsEligibleTld(fqdn: string): boolean {
  const { FF_TLDPUBLIC_API } = useFeatureFlags();

  const { summaries: hostingPackTlds } = useResourceFetch(
    tldPricesResource,
    {
      pack: 'website',
    },
    { enabled: FF_TLDPUBLIC_API },
  );

  if (!FF_TLDPUBLIC_API || !hostingPackTlds) {
    // If the API is not available, we fallback to a basic check
    return (
      fqdn.endsWith('.net') || fqdn.endsWith('.org') || fqdn.endsWith('.fr')
    );
  }

  return !!hostingPackTlds && isTldEligible(fqdn, hostingPackTlds);
}

/** Temporary frontend-side check until ready from the API:
 *  https://gitlab.corp.gandi.net/gandi/squad-shop/external-issues/-/issues/83
 */
export function useIsEligibleForXsell(fqdn: string): {
  isEligibleTld: boolean,
  isRetail: boolean,
  /** True if all eligibility conditions are true */
  isEligibleForXsell: boolean,
} {
  const isRetail = useIsRetailCustomer();
  const isEligibleTld = useIsEligibleTld(fqdn);

  const isEligibleForXsell = isRetail && isEligibleTld;

  return {
    isRetail,
    isEligibleTld,
    isEligibleForXsell,
  };
}

export function useIsEligibleForEssential(fqdn: string): boolean {
  const { FF_WH_ESSENTIAL } = useFeatureFlags();
  const isRetail = useIsRetailCustomer();
  return FF_WH_ESSENTIAL && isRetail && fqdn.endsWith('.fr');
}
